import {
    HorseRaceEvent,
    HorseRacingPrompt,
    HorseRaceWagerType,
    PlatformBetOfferType,
    PlatformEvent,
    PlatformOffer,
    SelectedOutcome,
    HorseSelectedOutcome,
} from "@freeplay/shared-code";
import React from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { Group } from "../picks/Group";
import { GlobalStyles } from "../style/Style";
import { OutcomeInfoDisplay, PlatformEventStatusEnum } from "./OutcomeInfoDisplay";
import { OutcomeOptions } from "./OutcomeOptions";
import { PickEmOutcomeDisplay } from "./PickEmOutcomeDisplay";
import { SimpleHorseRacingOutcomeDisplay } from "./SimpleHorseRacingOutcomeDisplay";

interface BetGroupOutcomeDisplayProps {
    isUser?: boolean;
    testID?: string;
    gameEntries?: [PlatformEvent, PlatformOffer][];
    gameEntryWithHorseEvents?: [HorseRaceEvent, HorseRacingPrompt][];
    groupName: string;
    groupDate: Date;
    betOutcomes?: SelectedOutcome[];
    horseBetOutcomes?: HorseSelectedOutcome[];
    startIndex?: number;
}

export const BetGroupOutcomeDisplay: React.FC<BetGroupOutcomeDisplayProps> = ({
    isUser,
    testID,
    gameEntries,
    gameEntryWithHorseEvents,
    groupName,
    groupDate,
    betOutcomes,
    horseBetOutcomes,
    startIndex = 0,
}) => {
    const styles = useDynamicStyles(generateStyles);

    const displayHorseRacingProps = () => {
        return gameEntryWithHorseEvents?.map((entry, index) => {
            switch (entry[1].propType) {
                case HorseRaceWagerType.PICKEM: {
                    return (
                        <PickEmOutcomeDisplay
                            key={`horse-racing-prop-${index + startIndex}`}
                            numberOfPlaces={entry[1].pickEm}
                            propString={entry[0].raceStatus === "COMPLETE" ? "Finish Order" : "To Finish"}
                            event={entry[0]}
                            selectedHorses={horseBetOutcomes?.[index + startIndex].choices}
                            horsePlacements={entry[0].raceFinishOrder}
                        />
                    );
                }
                case HorseRaceWagerType.HORSETOWIN:
                case HorseRaceWagerType.TOP4:
                case HorseRaceWagerType.WINNINGTIME:
                case HorseRaceWagerType.WINNINGDISTANCE: {
                    const winIndex = entry[1] && entry[1].correctResponse ? entry[1].correctResponse[0] : null;
                    return (
                        <SimpleHorseRacingOutcomeDisplay
                            key={`horse-racing-prop-${index + startIndex}`}
                            propType={entry[1].propType}
                            propValue={entry[1]}
                            winIndex={winIndex} // ! this should be null if not resulted or a number -1 - 1 or something
                            currentPick={horseBetOutcomes?.[index + startIndex].choices[0]}
                        />
                    );
                }
                default:
                    return <></>;
            }
        });
    };

    return (
        <Group matchTitle={groupName} groupDate={groupDate}>
            {gameEntries && <OutcomeInfoDisplay outcome={gameEntries[0][0]} isUser={isUser} />}
            {gameEntries?.map((entry, index) => {
                let descriptor = "To Win";

                if (entry[1].betOfferType === PlatformBetOfferType.HANDICAP) {
                    descriptor = "With Handicap";
                }
                if (entry[1].betOfferType === PlatformBetOfferType.OVERUNDER) {
                    descriptor = "Total Points";
                }
                return (
                    <React.Fragment key={`sports-type-prop-${index}`}>
                        <Text style={styles.descriptor}>{descriptor}</Text>
                        <View style={styles.container}>
                            <OutcomeOptions
                                key={`outcome-option-${index}-${entry[0].id}`}
                                gameOffer={entry[1]}
                                betOutcomes={betOutcomes}
                                resulted={entry[0].platformEventStatus === PlatformEventStatusEnum.SCORED}
                            />
                            <View style={[styles.atBox]}>
                                <Text style={[styles.atText]}>
                                    {entry[1].betOfferType === PlatformBetOfferType.OVERUNDER ? "OR" : "AT"}
                                </Text>
                            </View>
                        </View>
                    </React.Fragment>
                );
            })}
            {gameEntryWithHorseEvents && gameEntryWithHorseEvents?.length > 0 && <>{displayHorseRacingProps()}</>}
        </Group>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        container: {
            width: "100%",
            flexDirection: "row",
            marginBottom: 10,
        },
        atBox: {
            position: "absolute",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.AT_BOX_BACKGROUND),
            width: 54,
            height: 54,
            bottom: 6,
            left: 0,
            right: 0,
            margin: "auto",
            borderRadius: "100%",
            borderColor: () => EStyleSheet.value(GlobalStyles.AT_BOX_BORDER),
            borderWidth: 2,
            justifyContent: "center",
            alignItems: "center",
        },
        atText: {
            color: () => EStyleSheet.value(GlobalStyles.PROP_AT_TEXT),
            fontSize: 14,
            lineHeight: 14,
            fontWeight: "700",
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
        },
        descriptor: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
            fontSize: 12,
            fontWeight: "700",
            textAlign: "center",
            lineHeight: 12,
            marginBottom: 5,
        },
    });
};
